import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  trendMetricsForPlatform,
  type PaidMediaOverviewGranularity,
  type PaidMediaOverviewTrendPoint,
} from "src/services/PaidMediaOverviewAPI";
import { formatOverviewNumber, formatOverviewText } from "../../utils/paidMediaOverviewFormat";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import { OVERVIEW_PLATFORM_DESCRIPTORS } from "../../data/paidMediaOverviewPlatforms";
import {
  OverviewChartEmptyState,
  OverviewChartLoadingState,
  OverviewMetricTip,
} from "./overviewPrimitives";
import {
  formatOverviewBucketLabel,
  OVERVIEW_GRID_COLOR,
  overviewAxisProps,
} from "./overviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";

interface PurchasesChartRow {
  date: string;
  [seriesKey: string]: string | number | null;
}

const purchasesKey = (slug: string) => `${slug}Purchases`;
const fillId = (slug: string) => `paidMediaOverviewPurchasesFill-${slug}`;

interface PurchasesTrendCardProps {
  series: PaidMediaOverviewTrendPoint[];
  granularity: PaidMediaOverviewGranularity;
  purchasesPeriodTotal: string | null;
  loading: boolean;
}

const PurchasesTrendCard: FC<PurchasesTrendCardProps> = ({
  series,
  granularity,
  purchasesPeriodTotal,
  loading,
}) => {
  const { t, i18n } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  const data: PurchasesChartRow[] = useMemo(
    () =>
      (series ?? []).map(point => {
        const row: PurchasesChartRow = {
          date: formatOverviewBucketLabel(
            point?.date ?? "",
            granularity,
            date => t("paidMediaOverview.trend.weekOf", { date }),
            i18n.language
          ),
        };
        OVERVIEW_PLATFORM_DESCRIPTORS.forEach(descriptor => {
          row[purchasesKey(descriptor.slug)] =
            trendMetricsForPlatform(point, descriptor.key)?.purchases ?? null;
        });
        return row;
      }),
    [series, granularity, t, i18n.language]
  );

  const renderChart = () => {
    if (loading) {
      return <OverviewChartLoadingState />;
    }
    if (!data.length) {
      return <OverviewChartEmptyState />;
    }

    return (
      <ResponsiveContainer>
        <AreaChart data={data} margin={chartRtl.margin({ top: 5, right: 10, bottom: 5, left: -15 })}>
          <defs>
            {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
              <linearGradient
                key={descriptor.key}
                id={fillId(descriptor.slug)}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={descriptor.color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={descriptor.color} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
          <XAxis dataKey="date" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
          <YAxis
            orientation={chartRtl.yAxisOrientation}
            tickFormatter={value => formatOverviewNumber(Number(value))}
            {...overviewAxisProps}
          />
          <Tooltip
            wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
            content={
              <CustomChartTooltip formatTooltip={(value: number) => formatOverviewNumber(value)} />
            }
          />
          {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
            <Area
              key={descriptor.key}
              type="monotone"
              dataKey={purchasesKey(descriptor.slug)}
              name={t("paidMediaOverview.conversion.seriesPurchasesFor", {
                platform: t(descriptor.labelKey),
              })}
              stroke={descriptor.color}
              fill={`url(#${fillId(descriptor.slug)})`}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  return (
    <article className="paid-media-panel">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>
            {t("paidMediaOverview.conversion.purchasesTitle")}
            <OverviewMetricTip
              id="paidPurchases"
              label={t("paidMediaOverview.conversion.purchasesTitle")}
            />
          </h3>
          <p className="paid-media-overview__panel-subtitle">
            {t("paidMediaOverview.conversion.purchasesSubtitle")}
          </p>
        </div>
        <span className="paid-media-overview__panel-header-note">
          {t("paidMediaOverview.conversion.periodTotal", {
            value: formatOverviewText(purchasesPeriodTotal),
          })}
        </span>
      </div>
      <div className="paid-media-overview__chart paid-media-overview__chart--md">
        {renderChart()}
      </div>
    </article>
  );
};

export default PurchasesTrendCard;
